import { useContext, useMemo, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import restApi from '../lib/restApi';
import getRoute from '../utils/getRoute';
// eslint-disable-next-line import/no-cycle
import { Context } from '../context/AppContext';

const injectSchema = data => {
  const { schema } = data || {};
  if (!schema) return;

  const elms = document.getElementsByClassName('ls-structured-data-schema');

  [...elms].forEach(elm => {
    elm.remove();
  });

  schema.forEach(item => {
    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.className = 'ls-structured-data-schema';
    tag.innerText = JSON.stringify(item);
    document.head.appendChild(tag);
  });
};

const useContextApi = ssr => {
  const { pathname, search } = useLocation();
  const params = useParams();
  const route = getRoute(pathname);

  const {
    isServer,
    setData,
    cookie,
    userAgent: { userAgent } = {},
    ...context
  } = useContext(Context) || {};

  const apiArray = useMemo(() => {
    const query = queryString.parse(search || '');

    return ssr({
      params,
      query,
      pathname,
      cookies: cookie.getCookieAll(),
      setContext: () => {},
      redirect: () => {},
      userAgent,
    });
  }, [pathname, search]);

  const defaults = (apiArray || []).reduce(
    (acc, { api }) => ({
      ...acc,
      [api]: {
        loading: false,
        error: false,
        option: {},
      },
    }),
    {}
  );

  const routeData = (context[route] && context[route].data) || defaults;

  const setContextData = (api, data) => {
    setData(latestContext => {
      const rd =
        (latestContext[route] && latestContext[route].data) || defaults;

      return {
        ...latestContext,
        [route]: {
          ...latestContext[route],
          data: {
            ...rd,
            [api]: {
              ...rd[api],
              ...data,
            },
          },
        },
      };
    });
  };

  const fetch = async (api, newOptions, callback = () => {}) => {
    const { options: prevOptions } = apiArray.find(item => item.api);
    const options = newOptions || prevOptions;

    const setApiData = async data => {
      const callbackData = await callback(data);
      injectSchema(callbackData);
      setContextData(
        api,
        callbackData && callbackData.data
          ? { ...callbackData.data, options }
          : {
              ...data,
              options,
            }
      );
    };

    const setLoading = loading => {
      setContextData(api, { loading });
    };

    const setError = status => {
      setContextData(api, {
        error: status,
        loading: false,
      });
    };

    await restApi(api, options, setApiData, setLoading, setError);
  };

  const objectCompare = (obj, compare) =>
    Object.keys(obj).some(key => obj[key] !== compare[key]);

  useEffect(() => {
    if (isServer) {
      return;
    }
    apiArray.forEach(({ options = {}, api, callback }) => {
      const prevOptions = (routeData[api] && routeData[api].options) || {};

      const diffLength =
        Object.keys(options.params || {}).length !==
        Object.keys(prevOptions.params || {}).length;

      const hasChanged = objectCompare(
        options.params || {},
        prevOptions.params || {}
      );
      if (diffLength || hasChanged) {
        fetch(api, options, callback);
      }
    });
  }, [apiArray]);
  return { ...routeData, fetch };
};

export default useContextApi;
